import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import React, { Fragment } from "react";

import { getLatestAssignment } from "@/assignments/utils/getLatestAssignment/getLatestAssignment";
import RewardsTotal from "@/challenges/RewardsTotal";
import {
  SIZE_TYPES,
  VALUE_TYPES,
} from "@/challenges/RewardsTotal/RewardsTotal.style";
import useArticleGetByContentId from "@/challenges/utils/useArticleGetByContentId";
import useIsAuthorizedForIndependentReadingChallenge from "@/challenges/utils/useIsAuthorizedForIndependentReadingChallenge/useIsAuthorizedForIndependentReadingChallenge";
import ExportablesModal from "@/components/AIComponents/ExportablesModal";
import useHasFeature from "@/features/useHasFeature";
import canViewGradingPage from "@/grading/utils/canViewGradingPage";
import CheckCircleFilled from "@/icons/CheckCircleFilled";
import activityTypes from "@/view/constants/activityTypes";
import assessmentTypes from "@/view/constants/assessmentTypes";
import contentTypes from "@/view/constants/contentTypes";
import uiTypes from "@/view/constants/uiTypes";
import filterAttachmentsByLanguage from "@/view/utils/filterAttachmentsByLanguage";
import getActivityPanelNavigationHref from "@/view/utils/getActivityPanelNavigationHref";
import getActivitySubjectName from "@/view/utils/getActivitySubjectName";
import getEventPropertiesContentType from "@/view/utils/getEventPropertiesContentType";
import getGuidedHighlightingQuizAttachment from "@/view/utils/getGuidedHighlightingQuizAttachment";
import groupAttachmentsBySubjectType from "@/view/utils/groupAttachmentsBySubjectType";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";
import { useExportablesContext } from "@/components/AIComponents/ExportablesModal/ExportablesContextProvider/useExportablesContext";
import ActivityHeader from "../ActivityHeader";
import { CONFIG_KEYS } from "../AdditionalAssignmentSettings/AdditionalAssignmentSettings.constants";
import { GUIDED_HIGHLIGHTING_ACTIVITY_PANEL } from "../GuidedHighlightingContainer/utils/utils";
import OmittedQuizMessage from "../OmittedQuizMessage";
import PanelHeader from "../PanelHeader";
import getAssignmentSetting from "../utils/getAssignmentSetting";
import useGuidedHighlightingStudent from "../utils/useGuidedHighlightingStudent";
import ActivityPickerModal from "./ActivityPickerModal";
import {
  activityInfoContentCss,
  activityInfoContentTextCss,
  attachmentsGroupDividerCss,
  challengesRewardsContentCss,
  containerCss,
  dividerCss,
  headerCss,
  iconCss,
  listContainerCss,
  subjectTitleCss,
} from "./ArticleActivityPicker.style";
import ArticleActivityPickerOption from "./ArticleActivityPickerOption";
import AssignmentOption from "./AssignmentOption";
import AttachmentsErrorMessage from "./AttachmentsErrorMessage/AttachmentsErrorMessage";
import ChallengeEarnedTokenContent from "./ChallengeEarnedTokenContent";
import ChallengeTokensInfoText from "./ChallengeTokensInfoText";
import filterActivitiesById from "./utils/filterActivitiesById";

const propTypes = {
  assignments: PropTypes.array,
  assignmentsLoading: PropTypes.bool,
  attachments: PropTypes.arrayOf(PropTypes.object),
  attachmentsError: PropTypes.object,
  closeButtonEventProperties: PropTypes.object,
  contentId: PropTypes.string,
  contentMetadata: PropTypes.arrayOf(PropTypes.object),
  content: PropTypes.object,
  contentType: PropTypes.string,
  currentLevel: PropTypes.object,
  hideAssignments: PropTypes.bool,
  language: PropTypes.string,
  legacyArticleLevelHeaderId: PropTypes.number,
  modalTriggerRef: PropTypes.object,
  onClickCloseButton: PropTypes.func,
  quizOmissionReason: PropTypes.string,
  shouldFocusCloseButtonRef: PropTypes.object,
  userProperties: PropTypes.object,
};

const { QUIZ, POWER_WORDS_ACTIVITY } = assessmentTypes;

const { INTERACTIVE_VIDEO } = contentTypes;

const AssignmentOptionWithTrackEvent = withTrackEvent(AssignmentOption);

const actionPrefix = actionPrefixes.initiate;
const actionName = actionNames.navigate;

const ArticleActivityPicker = ({
  assignments,
  assignmentsLoading,
  attachments,
  attachmentsError,
  closeButtonEventProperties,
  content,
  contentId,
  contentMetadata,
  currentLevel,
  hideAssignments,
  language,
  legacyArticleLevelHeaderId,
  contentType,
  quizOmissionReason,
  onClickCloseButton,
  shouldFocusCloseButtonRef,
  userProperties,
}) => {
  const router = useRouter();
  const { isPresentModeActive } = usePresentModeContext();
  const { isStudentPreviewOn } = useStudentPreviewContext();
  const isAuthorizedGradingPageUser = canViewGradingPage(userProperties);
  const [isGradingPageFeatureEnabled] = useHasFeature("grading_page");
  const isAuthorizedChallengesPageUser =
    useIsAuthorizedForIndependentReadingChallenge();
  const { visibleActivityIds } = useAttachmentsContext();
  const { uiType } = useUserContentUIContext();
  const { data: articleData } = useArticleGetByContentId(contentId);
  const { isFeatureFlagActive } = useGuidedHighlightingStudent();
  const { templateList: assignedGraphicOrganizers } = useExportablesContext();

  const { isStudent, canViewPowerWords, activeELALicense, isLiteUser } =
    userProperties;

  const liteUserLiteContent = uiType === uiTypes.LITE;

  // TODO: Determine if this can be deferred until the button itself is clicked.
  // Not a blocker for experiment launch.

  const levelId = currentLevel?.id;

  const { pathname, query } = router;

  const handleClick = (href) => {
    router.replace(href, null, { shallow: true });
  };

  const renderHeader = (headerString) => {
    return headerString ? <h3 css={headerCss}>{headerString}</h3> : null;
  };

  const renderActivityOptions = (activities) => {
    if (!activities.length) return null;
    return activities.map((attachment, index) => {
      const { data } = attachment;
      const { assessment_type: assessmentType } = data;

      // TODO: Once feature flag 'lite-trial-premium-2023' is removed, we will no longer need to check for `canViewPowerWords`.
      const shouldHidePowerWords =
        (isStudent && liteUserLiteContent) ||
        (!isLiteUser && !canViewPowerWords);

      if (shouldHidePowerWords && assessmentType === POWER_WORDS_ACTIVITY.type)
        return null;

      const href = getActivityPanelNavigationHref({
        attachment,
        isStudent,
        pathname,
        query,
      });

      const ircActivityTypes = [QUIZ.type, POWER_WORDS_ACTIVITY.type];
      const isActivityInIrcExperiment =
        isAuthorizedChallengesPageUser &&
        ircActivityTypes.includes(assessmentType);

      const Icon =
        isActivityInIrcExperiment && articleData?.data
          ? CheckCircleFilled
          : activityTypes[assessmentType]?.icon;

      const eventProperties = {
        objectType: activityTypes[assessmentType]?.objectType,
        actionPrefix,
        actionName,
        streamProperties: {
          content_type: "Article",
          description: `Open ${activityTypes[assessmentType]?.objectType} activity panel.`,
          content_id: levelId,
          object_id: attachment.id,
        },
      };

      const currentActivity = articleData?.data?.interactions?.filter(
        (interaction) =>
          interaction.type ===
          activityTypes[assessmentType].label.toLowerCase().replace(" ", "_"),
      )[0];

      return (
        <Fragment key={`${attachment.id}-${index}`}>
          <ArticleActivityPickerOption
            eventProperties={eventProperties}
            onClick={() => handleClick(href)}
            isAuthorizedChallengesPageUser={isAuthorizedChallengesPageUser}
            isPremiumOption={assessmentType === POWER_WORDS_ACTIVITY.type}
          >
            <span css={activityInfoContentCss}>
              {(Icon || (isActivityInIrcExperiment && articleData?.data)) && (
                <Icon
                  css={iconCss({
                    ircStyleEnabled:
                      isActivityInIrcExperiment && articleData?.data,
                    isPowerWord: assessmentType === POWER_WORDS_ACTIVITY.type,
                    isDoneActivity: currentActivity?.is_done,
                  })}
                />
              )}
              <p css={activityInfoContentTextCss}>
                {activityTypes[assessmentType]?.label}
              </p>
            </span>
            {isActivityInIrcExperiment && (
              <span css={challengesRewardsContentCss}>
                <RewardsTotal
                  sizeVersion={
                    isStudent ? SIZE_TYPES.SMALL : SIZE_TYPES.EXTRA_SMALL
                  }
                  valueVersion={
                    isStudent
                      ? VALUE_TYPES.DOUBLE_VALUE
                      : VALUE_TYPES.SINGLE_VALUE
                  }
                  articleData={articleData}
                  type={activityTypes[assessmentType]?.label}
                />
              </span>
            )}
          </ArticleActivityPickerOption>
        </Fragment>
      );
    });
  };

  const renderActivityGroups = () => {
    if (!quizOmissionReason && attachmentsError) {
      return <AttachmentsErrorMessage />;
    }
    const attachmentsWithoutGuidedHighlightingQuiz = attachments?.filter(
      (attachment) =>
        attachment.data?.assessment_type !== "GUIDED_HIGHLIGHTING_QUIZ",
    );
    const filteredAttachmentsByLanguage = filterAttachmentsByLanguage(
      attachmentsWithoutGuidedHighlightingQuiz,
      language,
    );
    const attachmentsFilteredForAssignmentActivitySelections =
      filterActivitiesById(filteredAttachmentsByLanguage, visibleActivityIds);

    if (
      !attachmentsFilteredForAssignmentActivitySelections ||
      !attachmentsFilteredForAssignmentActivitySelections.length
    ) {
      return null;
    }
    const attachmentsGroupedBySubject = groupAttachmentsBySubjectType(
      attachmentsFilteredForAssignmentActivitySelections,
    );

    return (
      <>
        {Object.entries(attachmentsGroupedBySubject).map(
          ([subject, activities], index) => (
            <Fragment key={subject}>
              {subject !== "undefined" && (
                <h3 css={subjectTitleCss}>
                  {getActivitySubjectName(subject, activeELALicense)}
                </h3>
              )}
              {isAuthorizedChallengesPageUser && activities?.length > 1 && (
                <ChallengeTokensInfoText
                  isStudent={isStudent}
                  articleData={articleData}
                />
              )}
              <ul
                role="list"
                css={containerCss(isAuthorizedChallengesPageUser)}
              >
                {renderActivityOptions(activities)}
              </ul>
              {index ===
              Object.entries(attachmentsGroupedBySubject).length - 1 ? null : (
                <div css={attachmentsGroupDividerCss} />
              )}
            </Fragment>
          ),
        )}
        {isAuthorizedChallengesPageUser && (
          <ChallengeEarnedTokenContent
            isStudent={isStudent}
            articleData={articleData}
          />
        )}
      </>
    );
  };

  const renderAssignmentOptions = () => {
    if (
      hideAssignments ||
      !isStudent ||
      assignmentsLoading ||
      assignments?.length === 0
    ) {
      return null;
    }

    return (
      <>
        {renderHeader("Assignments")}
        <ul role="list" css={containerCss}>
          {assignments &&
            assignments.map((assignment, index) => {
              const activityTypeUrlFormatted = "assignment";
              const href = {
                pathname,
                query: {
                  ...query,
                  activityPanel: activityTypeUrlFormatted,
                  assignmentId: assignment.assignment_id,
                },
              };

              const eventProperties = {
                objectType: objectTypes.assignment,
                actionPrefix,
                actionName,
                streamProperties: {
                  content_type: "Article",
                  description: "Open assignment activity panel",
                  content_id: levelId,
                  object_id: assignment?.assignment_id,
                },
              };

              return (
                <AssignmentOptionWithTrackEvent
                  key={index}
                  assignmentData={assignment}
                  onClick={() => handleClick(href)}
                  {...eventProperties}
                />
              );
            })}
        </ul>
      </>
    );
  };

  const renderAnnotationsOption = () => {
    if (contentType === INTERACTIVE_VIDEO) return null;

    const Icon = activityTypes.ANNOTATIONS.icon;
    const href = {
      pathname,
      query: {
        ...query,
        activityPanel: "annotations",
      },
    };

    const eventProperties = {
      objectType: objectTypes.annotation,
      actionPrefix,
      actionName,
      streamProperties: {
        content_type: "Article",
        description: "Open annotations activity panel.",
        content_id: levelId,
      },
    };

    return (
      <ArticleActivityPickerOption
        onClick={() => handleClick(href)}
        eventProperties={eventProperties}
      >
        <span css={activityInfoContentCss}>
          <Icon css={iconCss({ addIconBorder: false, isPowerWord: false })} />
          {activityTypes.ANNOTATIONS.label}
        </span>
      </ArticleActivityPickerOption>
    );
  };

  const renderChecksForUnderstandingOption = () => {
    const Icon = activityTypes.CHECKS_FOR_UNDERSTANDING_QUIZ.icon;
    const href = {
      pathname,
      query: {
        ...query,
        activityPanel: "checks_for_understanding",
      },
    };

    const eventProperties = {
      objectType: "ChecksForUnderstanding",
      actionPrefix,
      actionName,
      streamProperties: {
        content_type: "Article",
        description: "Open checks for understanding quiz activity panel.",
        content_id: levelId,
      },
    };

    const latestAssignment = assignments[0];
    if (latestAssignment) {
      const hideCFUQuiz =
        latestAssignment.assignment_config.hide_checks_for_understanding;
      return hideCFUQuiz ? null : (
        <ArticleActivityPickerOption
          onClick={() => handleClick(href)}
          eventProperties={eventProperties}
        >
          <span css={activityInfoContentCss}>
            <Icon css={iconCss({ addIconBorder: false, isPowerWord: false })} />
            {activityTypes.CHECKS_FOR_UNDERSTANDING_QUIZ.label}
          </span>
        </ArticleActivityPickerOption>
      );
    }
  };

  const renderGradingOption = () => {
    const liteUserPremiumContent = uiType === uiTypes.LITE_USER_PREMIUM_CONTENT;
    const shouldNotShowGradingOption =
      !isAuthorizedGradingPageUser ||
      !isGradingPageFeatureEnabled ||
      isPresentModeActive ||
      isStudentPreviewOn ||
      liteUserPremiumContent;

    if (shouldNotShowGradingOption) return null;

    const eventProperties = {
      objectType: objectTypes.content,
      actionPrefix,
      actionName,
      streamProperties: {
        content_type: getEventPropertiesContentType(contentType),
        description: "Open review student work modal",
        content_id: contentId,
      },
    };

    return (
      <ActivityPickerModal
        assignments={assignments}
        assignmentsLoading={assignmentsLoading}
        legacyArticleLevelHeaderId={legacyArticleLevelHeaderId}
        eventProperties={eventProperties}
        contentId={contentId}
        levelId={levelId}
      />
    );
  };

  const renderQuizOmissionReason = () => {
    if (!quizOmissionReason) {
      return null;
    }

    return <OmittedQuizMessage quizOmissionReason={quizOmissionReason} />;
  };

  const renderGraphicOrganizerSection = () => {
    if (assignedGraphicOrganizers.length === 0) {
      return null;
    }

    return (
      <ExportablesModal.Student
        content={content}
        contentMetadata={contentMetadata}
        currentLevel={currentLevel}
        levelId={levelId}
      />
    );
  };

  const renderGuidedHighlightingSection = () => {
    const Icon = activityTypes.GUIDED_HIGHLIGHTING_QUIZ.icon;
    const href = {
      pathname,
      query: {
        ...query,
        activityPanel: GUIDED_HIGHLIGHTING_ACTIVITY_PANEL,
      },
    };

    const eventProperties = {
      objectType: "GuidedHighlighting",
      actionPrefix,
      actionName,
      streamProperties: {
        content_type: getEventPropertiesContentType(contentType),
        content_id: levelId,
        description: "View Guided Highlighting",
      },
    };

    const currentActivity = articleData?.data?.interactions?.filter(
      (interaction) =>
        interaction.type === activityTypes.GUIDED_HIGHLIGHTING_QUIZ.label,
    )[0];

    const guidedHighlightingQuizAttachment =
      getGuidedHighlightingQuizAttachment(attachments);
    const guidedHighlightingData =
      guidedHighlightingQuizAttachment?.data?.levels?.find(
        (level) => (level.article_level_id || level.articleLevelId) === levelId,
      );

    const assignment = getLatestAssignment(assignments);

    const isGuidedHighlightingFeatureFlagEnabled = isFeatureFlagActive();

    const isGuidedHighlightingEnabled =
      !!assignment &&
      !getAssignmentSetting(CONFIG_KEYS.hide_guided_highlighting, assignment);

    const shouldShowGuidedHighlightingButton =
      isGuidedHighlightingFeatureFlagEnabled &&
      isGuidedHighlightingEnabled &&
      guidedHighlightingData?.questions !== undefined;
    return !shouldShowGuidedHighlightingButton ? null : (
      <>
        <Fragment key={guidedHighlightingData?.id || 1}>
          <ArticleActivityPickerOption
            eventProperties={eventProperties}
            onClick={() => handleClick(href)}
            isAuthorizedChallengesPageUser={isAuthorizedChallengesPageUser}
          >
            <span css={activityInfoContentCss}>
              {(Icon ||
                (isAuthorizedChallengesPageUser && articleData?.data)) && (
                <Icon
                  css={iconCss({
                    ircStyleEnabled:
                      isAuthorizedChallengesPageUser && articleData?.data,
                    isDoneActivity: currentActivity?.is_done,
                  })}
                />
              )}
              <p css={activityInfoContentTextCss}>
                {activityTypes.GUIDED_HIGHLIGHTING_QUIZ.label}
              </p>
            </span>
            {isAuthorizedChallengesPageUser && (
              <span css={challengesRewardsContentCss}>
                <RewardsTotal
                  sizeVersion={
                    isStudent ? SIZE_TYPES.SMALL : SIZE_TYPES.EXTRA_SMALL
                  }
                  valueVersion={
                    isStudent
                      ? VALUE_TYPES.DOUBLE_VALUE
                      : VALUE_TYPES.SINGLE_VALUE
                  }
                  articleData={articleData}
                  type={activityTypes.GUIDED_HIGHLIGHTING_QUIZ.label}
                />
              </span>
            )}
          </ArticleActivityPickerOption>
        </Fragment>
        {isAuthorizedChallengesPageUser && (
          <ChallengeEarnedTokenContent
            isStudent={isStudent}
            articleData={articleData}
          />
        )}
      </>
    );
  };

  const renderArticleActivitiesSection = () => {
    const activities = [
      renderAnnotationsOption(),
      renderChecksForUnderstandingOption(),
      renderGuidedHighlightingSection(),
      renderGraphicOrganizerSection(),
    ];

    return (
      <ul css={containerCss(isAuthorizedChallengesPageUser)}>
        {[...activities]}
      </ul>
    );
  };

  // Filter the list and remove any falsy values
  const filteredListOfActivityOptions = [
    renderArticleActivitiesSection(),
    renderAssignmentOptions(),
    renderQuizOmissionReason(),
    renderActivityGroups(),
    renderGradingOption(),
  ].filter((list) => {
    return !!list;
  });

  return (
    <>
      <PanelHeader
        onClickCloseButton={onClickCloseButton}
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
      >
        <ActivityHeader isStudent={isStudent} />
      </PanelHeader>
      {filteredListOfActivityOptions.map((list, index, array) => {
        return (
          <React.Fragment key={`list-${index}`}>
            <div css={listContainerCss}>{list}</div>
            {index === array.length - 1 ? null : <div css={dividerCss} />}
          </React.Fragment>
        );
      })}
    </>
  );
};

ArticleActivityPicker.propTypes = propTypes;

export default ArticleActivityPicker;
